/* Editorial — community-contributed pieces served by the companion app
   (/apps/sansminds/editorial). Renders the approved feed with per-item tier
   locks, and a single-entry view when the URL carries ?entry=<id>. Gating is
   decided SERVER-SIDE: locked items never receive body/mediaUrl — the theme
   only renders the teaser + upgrade prompt the API allows. */
(function () {
  'use strict';

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function fmtDate(iso) {
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) { return ''; }
  }

  function lockIcon(root) {
    var tpl = root.querySelector('template[data-edt-lock]');
    if (tpl && tpl.content.firstElementChild) return tpl.content.firstElementChild.cloneNode(true);
    return el('span', null, '');
  }

  function pageUrl(params) {
    var u = new URL(window.location.href);
    u.searchParams.delete('entry');
    u.searchParams.delete('epage');
    Object.keys(params || {}).forEach(function (k) {
      if (params[k] != null) u.searchParams.set(k, params[k]);
    });
    return u.pathname + u.search;
  }

  function tierName(L, tier) {
    return (L.tiers && L.tiers[tier]) || tier;
  }

  /* Turn a submitted video URL into something embeddable. */
  function videoEmbed(url) {
    var m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{6,})/);
    if (m) return { kind: 'iframe', src: 'https://www.youtube.com/embed/' + m[1] };
    m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (m) return { kind: 'iframe', src: 'https://player.vimeo.com/video/' + m[1] };
    if (/\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url)) return { kind: 'video', src: url };
    return { kind: 'link', src: url };
  }

  function typeLabel(L, type) {
    return (L.types && L.types[type]) || type;
  }

  // ─── Feed ───────────────────────────────────────────────
  function renderCard(root, item, L) {
    var li = el('li', 'edt-card' + (item.locked ? ' is-locked' : ''));
    var a = el('a', 'edt-card__link');
    a.href = pageUrl({ entry: item.id });

    var media = el('div', 'edt-card__media');
    if (item.thumbnailUrl) {
      var img = el('img', 'edt-card__img');
      img.src = item.thumbnailUrl;
      img.alt = '';
      img.loading = 'lazy';
      img.onerror = function () {
        media.replaceChild(el('span', 'edt-card__glyph', typeLabel(L, item.type)), img);
      };
      media.appendChild(img);
    } else {
      media.appendChild(el('span', 'edt-card__glyph', typeLabel(L, item.type)));
    }
    if (item.locked) {
      var lock = el('span', 'edt-card__lock');
      lock.appendChild(lockIcon(root));
      lock.setAttribute('aria-hidden', 'true');
      media.appendChild(lock);
    }
    a.appendChild(media);

    var body = el('div', 'edt-card__body');
    var meta = el('p', 'edt-card__meta');
    meta.appendChild(el('span', 'edt-card__type', typeLabel(L, item.type)));
    if (item.locked) {
      var chip = el('span', 'edt-card__tier', tierName(L, item.requiredTier));
      meta.appendChild(chip);
    }
    body.appendChild(meta);
    body.appendChild(el('h3', 'edt-card__title', item.title));
    if (item.description) body.appendChild(el('p', 'edt-card__desc', item.description));
    var by = el('p', 'edt-card__by');
    by.appendChild(el('span', null, (L.by || 'By') + ' ' + item.creatorName));
    if (item.publishedAt) by.appendChild(el('span', 'edt-card__date', fmtDate(item.publishedAt)));
    body.appendChild(by);
    a.appendChild(body);

    li.appendChild(a);
    return li;
  }

  function renderFeed(root, data, L) {
    root.innerHTML = '';
    if (!data.items.length) {
      var empty = el('div', 'edt-empty');
      empty.appendChild(el('p', 'u-small u-muted', L.empty || 'No published pieces yet. Check back soon.'));
      root.appendChild(empty);
      return;
    }
    var list = el('ul', 'edt-grid');
    data.items.forEach(function (item) { list.appendChild(renderCard(root, item, L)); });
    root.appendChild(list);

    if (data.totalPages > 1) {
      var nav = el('nav', 'edt-pagination');
      nav.setAttribute('aria-label', 'Editorial pages');
      if (data.page > 1) {
        var prev = el('a', 'btn btn--small', L.prev || 'Previous');
        prev.href = pageUrl({ epage: data.page - 1 });
        nav.appendChild(prev);
      }
      nav.appendChild(el('span', 'edt-pagination__state u-mono u-small', data.page + ' / ' + data.totalPages));
      if (data.page < data.totalPages) {
        var next = el('a', 'btn btn--small', L.next || 'Next');
        next.href = pageUrl({ epage: data.page + 1 });
        nav.appendChild(next);
      }
      root.appendChild(nav);
    }
  }

  // ─── Single entry ───────────────────────────────────────
  function renderLocked(root, item, data, L) {
    var box = el('div', 'edt-locked');
    var head = el('p', 'edt-locked__label');
    head.appendChild(lockIcon(root));
    head.appendChild(document.createTextNode(' ' + (L.lockedLabel || 'Members only')));
    box.appendChild(head);
    var msg = (L.lockedText || 'This piece is available to %tier% members and above.')
      .replace('%tier%', tierName(L, item.requiredTier));
    box.appendChild(el('p', 'edt-locked__text', msg));

    var actions = el('div', 'edt-locked__actions');
    if (!data.viewerSignedIn && L.signinUrl) {
      var si = el('a', 'btn btn--small', L.signin || 'Sign in');
      si.href = L.signinUrl;
      actions.appendChild(si);
    }
    if (L.joinUrl) {
      var join = el('a', 'btn btn--small', L.join || 'View memberships');
      join.href = L.joinUrl;
      actions.appendChild(join);
    }
    if (actions.childNodes.length) box.appendChild(actions);
    return box;
  }

  function renderContent(item) {
    var wrap = el('div', 'edt-content');
    if (item.type === 'text' && item.body) {
      var art = el('div', 'edt-content__text');
      item.body.split(/\n{2,}/).forEach(function (para) {
        if (para.trim()) art.appendChild(el('p', null, para.trim()));
      });
      wrap.appendChild(art);
    } else if (item.type === 'image' && item.mediaUrl) {
      var img = el('img', 'edt-content__img');
      img.src = item.mediaUrl;
      img.alt = item.title;
      wrap.appendChild(img);
    } else if (item.type === 'audio' && item.mediaUrl) {
      var au = document.createElement('audio');
      au.controls = true;
      au.src = item.mediaUrl;
      au.className = 'edt-content__audio';
      wrap.appendChild(au);
    } else if (item.type === 'video' && item.mediaUrl) {
      var emb = videoEmbed(item.mediaUrl);
      if (emb.kind === 'iframe') {
        var frame = el('div', 'edt-content__frame');
        var ifr = document.createElement('iframe');
        ifr.src = emb.src;
        ifr.setAttribute('allowfullscreen', '');
        ifr.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
        ifr.title = item.title;
        frame.appendChild(ifr);
        wrap.appendChild(frame);
      } else if (emb.kind === 'video') {
        var vid = document.createElement('video');
        vid.controls = true;
        vid.src = emb.src;
        vid.className = 'edt-content__video';
        wrap.appendChild(vid);
      } else {
        var link = el('a', 'btn btn--small', 'Watch');
        link.href = emb.src;
        link.target = '_blank';
        link.rel = 'noopener';
        wrap.appendChild(link);
      }
    }
    return wrap;
  }

  function renderEntry(root, data, L) {
    var item = data.item;
    root.innerHTML = '';
    var art = el('article', 'edt-entry');

    var back = el('a', 'btn-link edt-entry__back', '← ' + (L.back || 'All editorial'));
    back.href = pageUrl({});
    art.appendChild(back);

    var meta = el('p', 'edt-entry__meta');
    meta.appendChild(el('span', 'edt-card__type', typeLabel(L, item.type)));
    if (item.publishedAt) meta.appendChild(el('span', 'edt-card__date', fmtDate(item.publishedAt)));
    art.appendChild(meta);

    art.appendChild(el('h2', 'edt-entry__title', item.title));
    art.appendChild(el('p', 'edt-entry__by', (L.by || 'By') + ' ' + item.creatorName));
    if (item.description) art.appendChild(el('p', 'edt-entry__desc', item.description));

    if (item.locked) {
      if (item.thumbnailUrl) {
        var tease = el('div', 'edt-entry__tease');
        var img = el('img', 'edt-card__img');
        img.src = item.thumbnailUrl;
        img.alt = '';
        tease.appendChild(img);
        art.appendChild(tease);
      }
      art.appendChild(renderLocked(root, item, data, L));
    } else {
      art.appendChild(renderContent(item));
    }

    root.appendChild(art);
  }

  // ─── Init ───────────────────────────────────────────────
  function initRoot(root) {
    var L = {};
    try { L = JSON.parse(root.getAttribute('data-labels') || '{}'); } catch (e) {}
    var proxy = root.getAttribute('data-proxy') || '/apps/sansminds';
    var params = new URLSearchParams(window.location.search);
    var entry = params.get('entry');
    var page = parseInt(params.get('epage')) || 1;

    var url = entry
      ? proxy + '/editorial/' + encodeURIComponent(entry)
      : proxy + '/editorial?page=' + page;

    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (r.status === 404) throw Object.assign(new Error('missing'), { missing: true });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (entry) renderEntry(root, data, L);
        else renderFeed(root, data, L);
      })
      .catch(function (err) {
        root.innerHTML = '';
        var msg = err && err.missing
          ? (L.notFound || 'That piece is no longer available.')
          : (L.error || 'Editorial is unavailable right now.');
        var p = el('p', 'u-small u-muted edt-error', msg);
        root.appendChild(p);
        if (err && err.missing) {
          var back = el('a', 'btn-link', '← ' + (L.back || 'All editorial'));
          back.href = pageUrl({});
          root.appendChild(back);
        }
      });
  }

  function init() {
    document.querySelectorAll('[data-editorial]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
